import { useCallback } from 'react';
import { Chip, Group } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import styles from './chipGroup.module.scss';

export type ChipGroupProps = {
  data: {
    disabled?: boolean;
    label: string;
    value: string;
  }[];
  defaultValue?: string | null;
  disabled?: boolean;
  value?: string | null;
  onChange?: (value: string | null) => void;
};

export function getChipGroupSelectedValue(
  value: string | string[] | null
): string | null {
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

export const ChipGroup = ({
  data,
  defaultValue,
  disabled = false,
  value,
  onChange,
}: ChipGroupProps) => {
  const [selected, setSelected] = useUncontrolled({
    value,
    defaultValue,
    onChange,
    finalValue: null,
  });

  const handleChange = useCallback(
    (value: string | string[] | null) => {
      if (disabled) {
        return;
      }
      const selectedValue = getChipGroupSelectedValue(value);
      setSelected(selectedValue);
    },
    [disabled, setSelected]
  );

  const handleChipClick = useCallback(
    (value: string, itemDisabled: boolean) => {
      if (!disabled && !itemDisabled && value === selected) {
        handleChange(null);
      }
    },
    [disabled, handleChange, selected]
  );

  return (
    <Chip.Group value={selected} onChange={handleChange}>
      <Group unstyled classNames={{ root: styles['chipGroup--root'] }}>
        {data.map((item) => (
          <Chip
            disabled={disabled || item.disabled}
            unstyled
            key={item.value}
            value={item.value}
            onClick={() => handleChipClick(item.value, item.disabled === true)}
            classNames={{
              checkIcon: styles['chip--checkIcon'],
              root: styles['chip--root'],
              label: styles['chip--label'],
            }}
          >
            <TextWithTooltip
              tooltipProps={{
                position: 'top',
              }}
              textProps={{ lineClamp: 1 }}
            >
              {item.label}
            </TextWithTooltip>
          </Chip>
        ))}
      </Group>
    </Chip.Group>
  );
};
